import { Box, Typography, Button, TextField, Grid, IconButton, Menu as PopupMenu, MenuItem, useMediaQuery, Tooltip } from "@mui/material";
import { FC, ReactNode, useEffect, useState } from "react";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { Menu, Expand } from "@mui/icons-material";
import { createSearchParams, useNavigate } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { ExcelExportParams, FilterModel, GridApi, IServerSideGetRowsRequest } from "ag-grid-enterprise";
import { classes } from "../GenericProGridStyles";
import { toastError } from "../../../utils/AppUtils";
import useAbortApi from "../../../Hooks/useAbortApi";
import { DEFAULT_COLID_DOTS, exportSelectedOnly, getSelectedRowsCustom } from "../../../utils/AGGridUtils";
import ViewSelector from "./ViewSelector/ViewSelector";
import { GetUATMenuItemNodes } from "../UATOrdersUtils/UATOrderUtils";
import { GetUATJourneyMenuItemNodes } from "../UATJourneyUtils/UATJourneyUtils";
import { runExportGridDataProcess } from "../../../config/axios/apis/grid/grid";
import { isHttpErrorMessageAvailable } from "../../../config/axios/Axios";
import { ExportGridIdentifier } from "../../../utils/appEnums/AppEnums";
import ExportProgressModal from "./ExportProgressModal/ExportProgressModal";
import ExportNameModal from "./ExportNameModal/ExportNameModal";

interface IProps {
    dataFetchingType: "gql" | "restApi" | "localData";
    tableTitle: string | ReactNode;
    gridApi: GridApi | null;
    editLink: string | ((rowData: any) => void);
    setClientSideRowData: any;
    restApiDataClientSide?: boolean;
    editLinkQueryParams?: any;
    deleteButtonFunc?: (id: number, rowData: any) => Promise<any>;
    deactivateButtonFunc?: (id: number) => Promise<any>;
    restApiRequest?: (config?: any) => Promise<any>;
    deactivateButtonActivationChecker?: string | ((rowData: any) => boolean);
    disableFullTextSearch?: boolean;
    disableEditing?: boolean;
    disableExporting?: boolean;
    additionalHamburgerMenuItems?: (params) => { name: string; action: () => void; disabled: boolean }[];
    // valueTypeConverterColumns?: string[];
    disableHeaderHamburgerMenu?: boolean;
    enableUATOrderActions: boolean;
    enableUATJourneyActions: boolean;
    enableViewSelector: boolean;
    enableOrderCommentsFlyIn: boolean;
    commentContextMenuAction: (rowData) => void;
    fullTextSearchLabel: ReactNode;
    fullTextSearchPlaceholder: string;
    exportIdentifier?: ExportGridIdentifier;
    requestFilterProcessorV3?: (params: IServerSideGetRowsRequest, initialFilters?: FilterModel) => any[];
    initialFilters?: FilterModel;
    dynamicFilters?: object;
    hasUserFilterableColumns: boolean;
}

const HeaderAndButton: FC<IProps> = ({
    gridApi,
    editLink,
    deleteButtonFunc,
    deactivateButtonFunc,
    setClientSideRowData,
    editLinkQueryParams,
    deactivateButtonActivationChecker,
    disableFullTextSearch,
    additionalHamburgerMenuItems,
    tableTitle,
    dataFetchingType,
    restApiRequest,
    disableEditing,
    disableExporting,
    restApiDataClientSide,
    // valueTypeConverterColumns,
    disableHeaderHamburgerMenu,
    enableUATOrderActions,
    enableUATJourneyActions,
    enableViewSelector,
    enableOrderCommentsFlyIn,
    commentContextMenuAction,
    fullTextSearchLabel,
    fullTextSearchPlaceholder,
    exportIdentifier,
    requestFilterProcessorV3,
    initialFilters,
    dynamicFilters,
    hasUserFilterableColumns,
}) => {
    const navigate = useNavigate();
    const isColumn = useMediaQuery("(max-width: 999px)");

    const { signal } = useAbortApi();
    const [columnsExpanded, setColumnsExpanded] = useState(false);
    const [selectedRows, setSelectedRows] = useState<any>([]);
    const [quickFilter, setQuickFilter] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const [openExportProgress, setOpenExportProgress] = useState<boolean>(false);
    const [sysProcessLogId, setSysProcessLogId] = useState<number>(0);
    const [openExportNameModal, setOpenExportNameModal] = useState(false);

    const [currentFilterModel, setCurrentFilterModel] = useState<FilterModel>({});

    useEffect(() => {
        if (!gridApi) return undefined;
        const api = gridApi;
        const onFilterChanged = () => setCurrentFilterModel(api.getFilterModel());
        setCurrentFilterModel(api.getFilterModel());
        api.addEventListener("filterChanged", onFilterChanged);
        return () => {
            if (api.isDestroyed()) return;
            api.removeEventListener("filterChanged", onFilterChanged);
        };
    }, [gridApi]);

    const getProtectedFilterKeys = (): Set<string> => {
        const keys = new Set<string>();
        const quickFilterKeys = new Set(["-", "restApiFts"]);
        Object.keys(initialFilters || {}).forEach((k) => keys.add(k));
        Object.keys(dynamicFilters || {}).forEach((k) => keys.add(k));
        gridApi?.getColumns()?.forEach((col) => {
            const colDef = col.getColDef();
            const colId = col.getColId();
            if (quickFilterKeys.has(colId)) return;
            if (!col.isVisible() && (colDef.lockVisible || colDef.suppressColumnsToolPanel)) {
                keys.add(colId);
            }
        });
        return keys;
    };

    const protectedKeys = getProtectedFilterKeys();
    const declaredDefaultFilters = { ...(initialFilters || {}), ...(dynamicFilters || {}) };
    const hasActiveUserFilters = (() => {
        if (quickFilter !== "") return true;
        if (Object.keys(currentFilterModel).some((key) => !protectedKeys.has(key))) return true;
        // Also detect when a declared default filter value has been modified by the user
        return Object.entries(declaredDefaultFilters).some(([key, defaultVal]) => JSON.stringify(currentFilterModel[key]) !== JSON.stringify(defaultVal));
    })();

    const handleClearFilters = () => {
        if (!gridApi) return;
        const quickFilterKeys = new Set(["-", "restApiFts"]);
        const currentModel = gridApi.getFilterModel();

        // Start from declared defaults — restores initialFilters/dynamicFilters to their intended values
        const newFilterModel: FilterModel = { ...declaredDefaultFilters };

        // Also preserve filters on always-hidden columns not covered by declaredDefaultFilters
        gridApi.getColumns()?.forEach((col) => {
            const colDef = col.getColDef();
            const colId = col.getColId();
            if (quickFilterKeys.has(colId)) return;
            if (!col.isVisible() && (colDef.lockVisible || colDef.suppressColumnsToolPanel)) {
                if (currentModel[colId] !== undefined && newFilterModel[colId] === undefined) {
                    newFilterModel[colId] = currentModel[colId];
                }
            }
        });

        gridApi.setFilterModel(newFilterModel);
        setQuickFilter("");
    };

    const gridResizeHandler = () => {
        setColumnsExpanded(false);
        gridApi!.sizeColumnsToFit();
    };

    useEffect(() => {
        if (gridApi) {
            window.addEventListener("resize", gridResizeHandler);
        }
        return () => {
            window.removeEventListener("resize", gridResizeHandler);
        };
    }, [gridApi]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        setSelectedRows(getSelectedRowsCustom(gridApi));
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const reloadGridData = () => {
        if (dataFetchingType === "gql") {
            gridApi!.refreshServerSide({ purge: true });
        } else if (restApiRequest) {
            restApiRequest()
                .then((res: any) => {
                    if (signal?.aborted) {
                        return;
                    }

                    setClientSideRowData(res?.data?.data);
                })
                .catch(() => {
                    toastError(<FormattedMessage id="common.error.api-message" defaultMessage="Something went wrong!" />);
                });
        }
    };

    const handleDelete = (selectedRowId: number) => {
        if (deleteButtonFunc) {
            deleteButtonFunc(selectedRowId, gridApi?.getRowNode(`${selectedRowId}`)?.data).then(() => {
                reloadGridData();
                gridApi!.deselectAll();
            });
        }
    };

    const handleDeactivate = (selectedRowId: number) => {
        if (deactivateButtonFunc) {
            deactivateButtonFunc(selectedRowId)
                .then(() => {
                    reloadGridData();
                })
                .catch(() => {
                    toastError(<FormattedMessage id="grid.error.deactivate" defaultMessage="Could not deactivate. Something went wrong!" />);
                });
        }
    };

    const shouldEnableDeactivateButton = (rowData) => {
        if (!deactivateButtonFunc) {
            return false;
        }

        if (typeof deactivateButtonActivationChecker === "function") {
            return deactivateButtonActivationChecker(rowData);
        }
        if (typeof deactivateButtonActivationChecker === "string") {
            return rowData[deactivateButtonActivationChecker];
        }

        return true;
    };

    useEffect(() => {
        const timer1 = setTimeout(() => {
            if (gridApi) {
                if (dataFetchingType === "gql") {
                    const filterModel = gridApi.getFilterModel();
                    gridApi.setFilterModel({
                        ...filterModel,
                        "-": { filter: quickFilter },
                    });
                    gridApi.refreshServerSide({ purge: true });
                } else if (dataFetchingType === "restApi" && !restApiDataClientSide) {
                    gridApi.setGridOption("context", { ...gridApi.getGridOption("context"), restApiFts: quickFilter });
                    gridApi.refreshServerSide({ purge: true });
                } else {
                    gridApi.setGridOption("quickFilterText", quickFilter);
                }
            }
        }, 1000);

        return () => {
            clearTimeout(timer1);
        };
    }, [quickFilter]);

    const resizeColumns = () => {
        if (columnsExpanded) {
            gridApi!.sizeColumnsToFit();
        } else {
            const allColumnIds: any = [];
            gridApi!.getColumns()!.forEach((column: any) => {
                allColumnIds.push(column.colId);
            });
            gridApi!.autoSizeColumns(allColumnIds, false);
        }
        setColumnsExpanded(!columnsExpanded);
    };

    const exportOption = () => {
        if (!disableExporting) {
            if (!exportIdentifier) {
                const onlySelected = exportSelectedOnly(gridApi);
                const exportConfig: ExcelExportParams = {
                    onlySelected,
                    columnKeys: gridApi!
                        .getColumns()!
                        .filter(
                            (element) => element.getColId() !== DEFAULT_COLID_DOTS && element.isVisible() && !element.getColDef().context?.suppressExcelExport,
                        )
                        .map((element) => element.getColId()),
                };

                // if (valueTypeConverterColumns) {
                //     exportConfig.processCellCallback = (params) => {
                //         const colId = params.column.getColId();
                //         if (valueTypeConverterColumns.length > 0 && valueTypeConverterColumns.includes(colId)) {
                //             const { cellRenderer } = params.column.getColDef();
                //             if (params.node) {
                //                 return cellRenderer({ value: params.value });
                //             }
                //         }
                //         const { value } = params;
                //         return value === undefined ? "" : value;
                //     };
                // }

                gridApi!.exportDataAsExcel(exportConfig);
                handleClose();
            } else if (
                exportIdentifier === ExportGridIdentifier.JOURNEY ||
                exportIdentifier === ExportGridIdentifier.MEMBER ||
                exportIdentifier === ExportGridIdentifier.SYMPHONY
            ) {
                setOpenExportNameModal(true);
            }
        }
    };

    const executeExport = (fileName) => {
        const filterState = gridApi?.getState()?.filter;
        let filterParams: any[] = [];
        if (typeof requestFilterProcessorV3 === "function") {
            if (filterState) {
                filterParams = requestFilterProcessorV3(
                    {
                        startRow: 0,
                        endRow: 100,
                        rowGroupCols: [],
                        valueCols: [],
                        pivotCols: [],
                        pivotMode: false,
                        groupKeys: [],
                        filterModel: filterState.filterModel!,
                        sortModel: [],
                    },
                    initialFilters,
                );
            }
        }

        const sortParams =
            gridApi?.getState()?.sort?.sortModel?.map((e) => ({
                key: e.colId,
                order: e.sort.toUpperCase(),
            })) || [];

        runExportGridDataProcess(exportIdentifier!, fileName || undefined, { filterBy: filterParams, orderBy: sortParams }, {}, true)
            .then((res) => {
                if (res.data) {
                    setSysProcessLogId(res.data);
                    setOpenExportProgress(true);
                }
            })
            .catch((error) => {
                if (!isHttpErrorMessageAvailable(error)) {
                    toastError(error?.response?.data?.message ? error?.response?.data?.message : error?.message);
                }
            });
    };

    const handleExportConfirm = (fileName) => {
        setOpenExportNameModal(false);
        executeExport(fileName);
    };

    return (
        <>
            {exportIdentifier && openExportProgress ? (
                <ExportProgressModal openModal={openExportProgress} closeModal={() => setOpenExportProgress(false)} sysProcessLogId={sysProcessLogId} />
            ) : null}
            {exportIdentifier && openExportNameModal ? (
                <ExportNameModal
                    open={openExportNameModal}
                    onClose={() => setOpenExportNameModal(false)}
                    onCancel={() => {
                        setOpenExportNameModal(false);
                        // executeExport("");
                    }}
                    onConfirm={handleExportConfirm}
                />
            ) : null}
            <Box className={classes.headerBox}>
                <Grid
                    className={classes.headerGrid}
                    container
                    sx={{
                        width: "100%",
                        ...(enableViewSelector ? { flexDirection: "column !important", gap: "8px" } : {}),
                    }}
                >
                    {enableViewSelector ? null : (
                        <Grid size={12}>
                            <Grid
                                container
                                sx={{
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                }}
                            >
                                <FactCheckIcon sx={{ marginRight: 1, color: "softBlack.hover" }} />
                                {tableTitle && (
                                    <Typography component="h1" variant="h6" sx={{ fontWeight: 600, color: "softBlack.hover" }}>
                                        {tableTitle}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    )}
                    <Grid
                        sx={{
                            width: "100%",
                        }}
                    >
                        {disableFullTextSearch ? null : (
                            <TextField
                                className={classes.filterField}
                                fullWidth
                                value={quickFilter}
                                type="text"
                                id="filter-text-box"
                                placeholder={fullTextSearchPlaceholder}
                                label={fullTextSearchLabel}
                                size="small"
                                onChange={(e: any) => setQuickFilter(e?.target?.value)}
                            />
                        )}
                    </Grid>
                    <Grid
                        sx={{
                            flexGrow: 1,
                            minWidth: 0,
                            width: "100%",
                        }}
                    >
                        <Grid
                            container
                            sx={{
                                justifyContent: "flex-end",
                                alignItems: "center",
                                flexGrow: 1,
                                flexWrap: "nowrap",
                                "@media(max-width: 799px)": { ...(enableViewSelector ? { flexWrap: "wrap" } : {}) },
                            }}
                        >
                            {enableViewSelector && <ViewSelector />}
                            <Grid
                                size={7}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    gap: "8px",
                                    marginLeft: "8px",
                                    marginRight: "0px !important",
                                    ...(isColumn ? { margin: "8px" } : {}),
                                }}
                            >
                                <Tooltip title={<FormattedMessage id="grid.actions.button.resize" defaultMessage="Resize" />} arrow>
                                    <Button
                                        variant="outlined"
                                        disableElevation
                                        className={classes.expandButton}
                                        onClick={() => resizeColumns()}
                                        id="expand-shrink-button"
                                        aria-label="expand-shrink"
                                    >
                                        <Expand className={classes.expandIcon} />
                                    </Button>
                                </Tooltip>
                                {hasUserFilterableColumns && (
                                    <Tooltip title={<FormattedMessage id="grid.actions.button.clear-filters" defaultMessage="Clear Filters" />} arrow>
                                        <span>
                                            <Button
                                                variant="outlined"
                                                disableElevation
                                                className={classes.expandButton}
                                                onClick={handleClearFilters}
                                                disabled={!hasActiveUserFilters}
                                                aria-label="clear-filters"
                                            >
                                                <FilterAltOffIcon />
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                                {disableHeaderHamburgerMenu ? null : (
                                    <Tooltip title={<FormattedMessage id="grid.actions.button.more-actions" defaultMessage="More Actions" />} arrow>
                                        <IconButton
                                            aria-label="more"
                                            id="long-button"
                                            aria-controls={open ? "basic-menu" : undefined}
                                            aria-expanded={open ? "true" : undefined}
                                            aria-haspopup="true"
                                            onClick={handleClick}
                                        >
                                            <Menu sx={{ color: "text.primary" }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Grid>
                            <PopupMenu
                                id="basic-menu"
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                                slotProps={{
                                    list: {
                                        "aria-labelledby": "basic-button",
                                    },
                                }}
                            >
                                {disableEditing || selectedRows?.length !== 1 ? null : (
                                    <MenuItem
                                        disabled={selectedRows?.length !== 1 || disableEditing}
                                        onClick={() => {
                                            if (selectedRows?.length === 1) {
                                                if (typeof editLink === "function") {
                                                    editLink(selectedRows?.[0]);
                                                } else {
                                                    navigate({
                                                        pathname: `${editLink}/${selectedRows?.[0]?.id}`,
                                                        search: `?${createSearchParams(editLinkQueryParams)}`,
                                                    });
                                                }
                                            }
                                            handleClose();
                                        }}
                                    >
                                        Edit
                                    </MenuItem>
                                )}
                                {selectedRows &&
                                    additionalHamburgerMenuItems?.(selectedRows)?.map((item) => {
                                        if (item?.disabled) {
                                            return null;
                                        }
                                        return (
                                            <MenuItem
                                                key={item?.name}
                                                disabled={item.disabled}
                                                onClick={() => {
                                                    item.action();
                                                    handleClose();
                                                }}
                                            >
                                                {item?.name}
                                            </MenuItem>
                                        );
                                    })}

                                {enableOrderCommentsFlyIn && selectedRows?.length === 1 && (
                                    <MenuItem
                                        onClick={() => {
                                            commentContextMenuAction(selectedRows?.[0]);
                                            handleClose();
                                        }}
                                    >
                                        <FormattedMessage id="grid.comments.label.context-option" defaultMessage="Comments" />
                                    </MenuItem>
                                )}

                                {deactivateButtonFunc && selectedRows?.length === 1 && shouldEnableDeactivateButton(selectedRows[0]) ? (
                                    <MenuItem
                                        disabled={selectedRows?.length !== 1 || !shouldEnableDeactivateButton(selectedRows[0])}
                                        onClick={() => {
                                            if (selectedRows?.length === 1) {
                                                handleDeactivate(selectedRows?.[0]?.id);
                                            }
                                            handleClose();
                                        }}
                                    >
                                        Deactivate This Product
                                    </MenuItem>
                                ) : null}
                                {deleteButtonFunc && selectedRows?.length === 1 ? (
                                    <MenuItem
                                        disabled={selectedRows?.length !== 1}
                                        onClick={() => {
                                            if (selectedRows?.length === 1) {
                                                handleDelete(selectedRows?.[0]?.id);
                                            }
                                            handleClose();
                                        }}
                                    >
                                        Delete
                                    </MenuItem>
                                ) : null}
                                {enableUATOrderActions && <GetUATMenuItemNodes selectedRows={selectedRows} action={handleClose} />}
                                {enableUATJourneyActions && <GetUATJourneyMenuItemNodes selectedRows={selectedRows} action={handleClose} />}
                                <MenuItem disabled={disableExporting} onClick={exportOption}>
                                    Export
                                </MenuItem>
                            </PopupMenu>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default HeaderAndButton;
